import { Controller } from '@hotwired/stimulus'
import { normalizeMarkdown } from '../utils/markdown'
import { safeMarked } from '../utils/safe_marked'

/**
 * Base controller for chatbot functionality.
 * Contains all shared logic for sending/receiving messages via ActionCable.
 *
 * Child controllers should extend this and implement:
 * - afterConnect() - for custom initialization
 * - renderConversationHistory() - for displaying conversation history UI
 * - showConversationError() - for displaying error messages
 */
export default class extends Controller {
  static targets = [
    'chatBox',
    'greeting',
    'chatInterface',
    'chatInputContainer',
    'chatInput',
    'chatMessages',
  ]

  static values = {
    conversationsPath: { type: String, default: '' },
    anonymous: { type: Boolean, default: false },
  }

  connect() {
    // Use the global ActionCable consumer if available
    this.consumer = window.CampusESP?.cable || null
    // Current conversation state (includes conversation_token, id, and title)
    this.currentConversation = null
    // Current ActionCable subscription
    this.currentSubscription = null
    // In-flight replies keyed by message_id, so each streams into its own bubble
    this.turns = new Map()
    // Stable session ID for the lifetime of this controller instance
    this.sessionId = crypto.randomUUID()

    // CSS class names (can be overridden by child controllers)
    this.cssClasses = {
      messageContainer: 'message-container',
      messageBubble: 'message-bubble',
      userMessage: 'user-message',
      assistantMessage: 'assistant-message',
      messageText: 'message-text',
      sourcesPillContainer: 'sources-pill-container',
      sourcesPill: 'sources-pill',
      sourcesPopup: 'sources-popup',
      sourcesPopupHeader: 'sources-popup-header',
      sourcesPopupList: 'sources-popup-list',
      sourceItem: 'source-item',
      sourceLink: 'source-link',
      sourceTitle: 'source-title',
      sourceMeta: 'source-meta',
    }

    // sessionStorage key for conversation persistence (override in child controllers)
    this._sessionStorageKey = 'chatbotConversationId'

    // Hook for child classes to customize initial state and CSS classes
    this.afterConnect()

    // Subscribe once — subscribed has no side effects, so reconnects are safe
    this.createChatbotSubscription()
  }

  disconnect() {
    this.cleanupSubscription()
  }

  /**
   * Hook for child classes to override for custom initialization
   */
  afterConnect() {
    // Default: show chat interface immediately
    if (this.hasChatInterfaceTarget) {
      this.chatInterfaceTarget.style.display = 'block'
    }
  }

  // ========================================
  // CORE CHATBOT LOGIC (all shared)
  // ========================================

  /**
   * Clean up the current ActionCable subscription
   */
  cleanupSubscription() {
    if (this.currentSubscription) {
      this.currentSubscription.unsubscribe()
      this.currentSubscription = null
    }
  }

  /**
   * Handle Enter key press for sending messages
   */
  handleEnterKey(event) {
    // Only handle Enter key, not Shift + Enter (which adds new line)
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      this.sendMessage()
    }
  }

  /**
   * Auto-resize textarea based on content
   */
  autoResizeTextarea() {
    const textarea = this.chatInputTarget
    const chatBox = this.chatBoxTarget

    // Check if user is at the bottom before resizing
    const isAtBottom =
      chatBox.scrollTop + chatBox.clientHeight >= chatBox.scrollHeight - 10

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto'

    // Set the height to match the scroll height,
    // with a max height of 120 pixels and a min height of 43 pixels
    const maxHeight = 120 // Maximum height in pixels (about 5-6 lines)
    const newHeight = Math.min(Math.max(textarea.scrollHeight, 43), maxHeight)

    textarea.style.height = newHeight + 'px'

    // Add scrollbar if content exceeds max height
    textarea.style.overflowY =
      textarea.scrollHeight > maxHeight ? 'auto' : 'hidden'

    // Update chat-box margin to match input container height
    chatBox.style.marginBottom =
      this.chatInputContainerTarget.offsetHeight + 'px'

    // Jump to bottom of chatbox if we were already at the bottom
    if (isAtBottom) {
      chatBox.scrollTop = chatBox.scrollHeight
    }
  }

  /**
   * Send a message to the chatbot
   */
  sendMessage() {
    const message = this.chatInputTarget.value.trim()

    if (message) {
      // Add user message bubble and clear input
      this.addMessage(message, 'user')
      this.chatInputTarget.value = ''

      // Reset textarea height after send a message
      this.autoResizeTextarea()

      if (!this.currentSubscription) {
        console.error('ActionCable subscription not available')
        return
      }

      this.startTurn(message)
    }
  }

  /**
   * Show a thinking placeholder for this message and enqueue its reply.
   * The reply streams back tagged with message_id and replaces the placeholder.
   */
  startTurn(message) {
    const messageId = crypto.randomUUID()
    this.turns.set(messageId, {
      element: this.showThinkingAnimation(),
      bubble: null,
      renderTimer: null,
    })

    // Using perform avoids the side-effectful subscribe pattern: ActionCable
    // reconnects re-call subscribed (safe, no-op) but never re-call perform.
    this.currentSubscription.perform('receive', {
      message,
      message_id: messageId,
      conversation_token: this.currentConversation?.conversation_token || null,
      persona_version: this.overridePersona(),
      ...this.extraPayloadData(),
    })
  }

  /**
   * Create a single persistent subscription for this controller instance.
   * Called once on connect — subscribed only registers the stream, so
   * ActionCable reconnects are safe and never re-trigger job enqueuing.
   */
  createChatbotSubscription() {
    if (!this.consumer) {
      console.error('ActionCable consumer not available')
      return
    }

    const self = this

    this.currentSubscription = this.consumer.subscriptions.create(
      {
        channel: 'ChatbotChannel',
        session_id: this.sessionId,
        source: this.subscriptionSource(),
      },
      {
        received(response) {
          // Unknown ids belong to a cleared chat or another tab's controller
          const turn = self.turns.get(response['message_id'])
          if (!turn) return

          const responseText = response['response']

          if (responseText === 'Response completed') {
            // Final response - render any remaining buffered content immediately
            if (turn.bubble) {
              clearTimeout(turn.renderTimer)
              self.renderMarkdown(turn.bubble)
            }
            self.turns.delete(response['message_id'])
          } else if (
            responseText &&
            responseText.startsWith('CONVERSATION_SUMMARY:')
          ) {
            // Add timestamp to the message
            if (self.useTimestamp) {
              self.addTimestamp(turn.bubble, 'assistant')
            }

            // Handle conversation summary display
            const summaryJson = responseText.replace(
              'CONVERSATION_SUMMARY: ',
              '',
            )
            if (summaryJson.trim()) {
              try {
                const summary = JSON.parse(summaryJson)

                // Update current conversation with data from backend
                // This ensures we have the correct conversation_token for future messages
                if (summary.conversation_token) {
                  if (!self.currentConversation) {
                    self.currentConversation = {
                      conversation_token: summary.conversation_token,
                      id: summary.conversation_id,
                      title: 'New Conversation',
                    }
                  } else {
                    // Update existing conversation data
                    self.currentConversation.conversation_token =
                      summary.conversation_token
                    self.currentConversation.id = summary.conversation_id
                  }

                  // Save conversation ID to sessionStorage for persistence across navigations
                  if (!self.anonymousValue) {
                    sessionStorage.setItem(
                      self._sessionStorageKey,
                      String(summary.conversation_id),
                    )
                  }

                  self.onConversationConfirmed(summary)

                  self.onConversationCreated(summary.conversation_id)
                }

                self.onAssistantResponse(summary.last_message_id)

                // Build sources list HTML for hover popup
                const sourcesCount =
                  summary.sources_used && summary.sources_used.length > 0
                    ? summary.sources_used.length
                    : 0
                const tokensUsed = summary.tokens_used || 0

                // Use addSourcesPill method to ensure consistent class names
                if (sourcesCount > 0) {
                  self.addSourcesPill(
                    summary.sources_used,
                    tokensUsed,
                    turn.element,
                  )
                }
              } catch (e) {
                console.error('Failed to parse conversation summary JSON:', e)
              }
            }
          } else if (responseText) {
            // Streaming content chunk - append to this turn's bot message
            if (!turn.bubble) {
              // Create container if configured
              const container = self.cssClasses.messageContainer
                ? document.createElement('div')
                : null

              if (container) {
                container.className = `${self.cssClasses.messageContainer} fade-in`
              }

              // Create new bot message bubble
              turn.bubble = document.createElement('div')
              const bubbleClasses = [self.cssClasses.messageBubble, 'fade-in']
              if (self.cssClasses.assistantMessage) {
                bubbleClasses.push(self.cssClasses.assistantMessage)
              }
              turn.bubble.className = bubbleClasses.filter(Boolean).join(' ')
              turn.bubble.dataset.markdownBuffer = '' // Store raw markdown

              // Add message text element if needed
              if (self.cssClasses.messageText) {
                const messageText = document.createElement('div')
                messageText.className = self.cssClasses.messageText
                turn.bubble.appendChild(messageText)
              }

              if (container) {
                container.appendChild(turn.bubble)
              }

              // Swap the thinking placeholder in place, keeping the reply under its question
              const element = container || turn.bubble
              turn.element.replaceWith(element)
              turn.element = element
            }

            // Append the chunk to the markdown buffer
            turn.bubble.dataset.markdownBuffer += responseText

            // Debounce the markdown rendering to avoid partial markdown syntax
            clearTimeout(turn.renderTimer)
            // Check if at bottom BEFORE render
            const wasNearBottom = self.isNearBottom()
            turn.renderTimer = setTimeout(() => {
              self.renderMarkdown(turn.bubble)
              // Auto-scroll during streaming if user was at the bottom
              if (wasNearBottom) {
                self.chatBoxTarget.scrollTop = self.chatBoxTarget.scrollHeight
              }
            }, 50) // 50ms debounce - fast enough to feel real-time, slow enough to batch chunks
          }
        },
      },
    )
  }

  /**
   * Helper to render markdown in a message bubble
   */
  renderMarkdown(bubble) {
    const rawMarkdown = bubble.dataset.markdownBuffer
    const markdownText = normalizeMarkdown(rawMarkdown)
    const target = this.cssClasses.messageText
      ? bubble.querySelector(`.${this.cssClasses.messageText}`)
      : bubble
    const container = target || bubble

    if (markdownText) {
      container.innerHTML = safeMarked.parse(markdownText)
      this.applyLinkTargets(container)
    } else {
      container.textContent = markdownText || ''
    }
  }

  /**
   * Creates and displays a chat message bubble for the given text and sender type
   */
  addMessage(text, sender) {
    // Create container if configured, otherwise append bubble directly
    const container = this.cssClasses.messageContainer
      ? document.createElement('div')
      : null

    if (container) {
      container.className = `${this.cssClasses.messageContainer} fade-in`
    }

    const bubble = document.createElement('div')
    const bubbleClasses = [this.cssClasses.messageBubble, 'fade-in']
    if (sender === 'user' && this.cssClasses.userMessage) {
      bubbleClasses.push(this.cssClasses.userMessage)
    } else if (sender !== 'user' && this.cssClasses.assistantMessage) {
      bubbleClasses.push(this.cssClasses.assistantMessage)
    }
    bubble.className = bubbleClasses.filter(Boolean).join(' ')

    // Create message text element if CSS class is defined
    const messageEl = this.cssClasses.messageText
      ? document.createElement('div')
      : bubble
    if (this.cssClasses.messageText) {
      messageEl.className = this.cssClasses.messageText
    }

    if (messageEl !== bubble) {
      bubble.appendChild(messageEl)
    }

    if (sender === 'user') {
      messageEl.textContent = text
      messageEl.style.whiteSpace = 'pre-wrap'
    } else {
      bubble.dataset.markdownBuffer = text
      this.renderMarkdown(bubble)
    }

    if (container) {
      container.appendChild(bubble)
      this.chatMessagesTarget.appendChild(container)
    } else {
      this.chatMessagesTarget.appendChild(bubble)
    }

    if (this.useTimestamp) {
      this.addTimestamp(bubble, 'user')
    }

    this.scrollToBottom()
  }

  /**
   * Show a thinking placeholder and return its outermost element
   */
  showThinkingAnimation() {
    const container = this.cssClasses.messageContainer
      ? document.createElement('div')
      : null

    if (container) {
      container.className = this.cssClasses.messageContainer
    }

    const bubble = document.createElement('div')
    const bubbleClasses = [
      this.cssClasses.messageBubble,
      'thinking-animation',
      'fade-in',
    ]
    if (this.cssClasses.assistantMessage) {
      bubbleClasses.push(this.cssClasses.assistantMessage)
    }
    bubble.className = bubbleClasses.filter(Boolean).join(' ')

    bubble.innerHTML = `
      <div class="thinking-indicator">
        <span class="thinking-text">Thinking</span>
        <div class="thinking-dots">
          <div class="thinking-dot"></div>
          <div class="thinking-dot"></div>
          <div class="thinking-dot"></div>
        </div>
      </div>
    `

    if (container) {
      container.appendChild(bubble)
      this.chatMessagesTarget.appendChild(container)
    } else {
      this.chatMessagesTarget.appendChild(bubble)
    }

    // Add CSS animation if not already added
    if (!document.getElementById('thinking-animation-style')) {
      const style = document.createElement('style')
      style.id = 'thinking-animation-style'
      style.textContent = `
        @keyframes thinking {
          0%, 80%, 100% {
            transform: scale(0.8);
            opacity: 0.5;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }
      `
      document.head.appendChild(style)
    }

    this.scrollToBottom()

    return container || bubble
  }

  // ========================================
  // SOURCES DISPLAY
  // ========================================

  truncateText(text, length) {
    return text.length > length ? `${text.slice(0, length - 1).trimEnd()}…` : text
  }

  /**
   * Helper method to build sources popup HTML
   */
  buildSourcesPopupHtml(sources) {
    let html = `<div class="${this.cssClasses.sourcesPopupList}">`
    sources.forEach((source) => {
      const title = this.escapeHtml(source.title || 'Unknown Source')
      const sourceType = source.source_type || 'unknown'
      const score = source.score ? Number(source.score).toFixed(2) : '0.00'
      const url = source.url
      const sourceScope = source.source_scope // 'global' or 'tenant'

      // Build meta line with source type, scope (for admins), and score (for admins)
      let metaParts = [sourceType]
      // A linked page names the post that links it, cut short so the card keeps one meta line
      if (source.linked_from) {
        metaParts[0] += ` · from “${this.escapeHtml(this.truncateText(source.linked_from, 40))}”`
      }
      if (sourceScope) {
        metaParts.push(
          `<span class="sources-data">• ${sourceScope.charAt(0).toUpperCase() + sourceScope.slice(1)}</span>`,
        )
      }
      metaParts.push(`<span class="sources-data">• Score: ${score}</span>`)
      const metaHtml = metaParts.join(' ')

      // Make the entire source item clickable if URL is available
      if (url) {
        html += `
          <a href="${url}" class="${this.cssClasses.sourceItem} ${this.cssClasses.sourceLink}">
            <div class="${this.cssClasses.sourceTitle}">${title}</div>
            <div class="${this.cssClasses.sourceMeta}">${metaHtml}</div>
          </a>
        `
      } else {
        html += `
          <div class="${this.cssClasses.sourceItem}">
            <div class="${this.cssClasses.sourceTitle}">${title}</div>
            <div class="${this.cssClasses.sourceMeta}">${metaHtml}</div>
          </div>
        `
      }
    })
    html += '</div>'
    return html
  }

  /**
   * Add sources pill to show sources used for an assistant message
   */
  addSourcesPill(sources, tokensUsed = 0, after = null) {
    // Check if at bottom BEFORE adding the element (adding increases scrollHeight)
    const wasNearBottom = this.isNearBottom()

    const sourcesCount = sources.length
    const sourcesPopupHtml = this.buildSourcesPopupHtml(sources)

    const summaryElement = document.createElement('div')
    summaryElement.className = `${this.cssClasses.sourcesPillContainer} fade-in`
    summaryElement.innerHTML = `
      <div class="${this.cssClasses.sourcesPill}" data-action="click->${this.identifier}#toggleSourcesPopup">
        <span class="fas fa-book"></span>
        ${sourcesCount} source${sourcesCount !== 1 ? 's' : ''}
      </div>
      <div class="${this.cssClasses.sourcesPopup}">
        <div class="${this.cssClasses.sourcesPopupHeader}">
          Sources <span class="sources-data"> | Tokens: ${tokensUsed.toLocaleString()}</span>
        </div>
        ${sourcesPopupHtml}
      </div>
    `
    this.applyLinkTargets(summaryElement)
    if (after) {
      after.after(summaryElement)
    } else {
      this.chatMessagesTarget.appendChild(summaryElement)
    }

    // Scroll to show the sources pill if user was near the bottom
    if (wasNearBottom) {
      this.chatBoxTarget.scrollTop = this.chatBoxTarget.scrollHeight
    }
  }

  // ========================================
  // CONVERSATION LOADING
  // ========================================

  /**
   * Fetch a conversation by ID and render its messages.
   * Child controllers call this from restoreFromSession() or history navigation.
   */
  async loadConversation(conversationId) {
    try {
      const response = await fetch(
        `/api/v1/ai_conversations/${conversationId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document
              .querySelector('meta[name="csrf-token"]')
              ?.getAttribute('content'),
          },
        },
      )

      if (!response.ok) {
        throw new Error('Failed to load conversation')
      }

      const conversation = await response.json()
      this.displayLoadedConversation(conversation)
    } catch (error) {
      console.error('Error loading conversation:', error)
      sessionStorage.removeItem(this._sessionStorageKey)
      this.showConversationError('Failed to load conversation')
    }
  }

  /**
   * Render a fetched conversation's messages into the chat area.
   * Override in child controllers to add UI-specific behavior (e.g. switching
   * to chat view in a modal) — call super first to render messages.
   */
  displayLoadedConversation(conversation) {
    this.currentConversation = conversation

    if (conversation.id) {
      sessionStorage.setItem(this._sessionStorageKey, String(conversation.id))
    }

    this.chatMessagesTarget.innerHTML = ''

    conversation.messages.forEach((message) => {
      if (message.role === 'user') {
        this.addMessage(message.content, 'user')
      } else if (message.role === 'assistant') {
        this.addMessage(message.content, 'assistant')

        if (message.sources_used && message.sources_used.length > 0) {
          this.addSourcesPill(message.sources_used, message.tokens_used || 0)
        }
      }
    })
  }

  /**
   * Hook for child controllers to display conversation load errors.
   */
  showConversationError(_message) {
    // No-op by default; override in child controllers with UI-specific error display
  }

  // ========================================
  // CONVERSATION HISTORY MANAGEMENT
  // ========================================

  /**
   * Clear chat messages and reset conversation state.
   * Override in child controllers to add UI-specific cleanup (e.g. feedback state).
   */
  clearChat() {
    this.currentConversation = null
    this.turns.forEach((turn) => clearTimeout(turn.renderTimer))
    this.turns.clear()
    this.chatInputTarget.value = ''
    this.autoResizeTextarea()
    sessionStorage.removeItem(this._sessionStorageKey)
    if (this.chatMessagesTarget) {
      this.chatMessagesTarget.innerHTML = ''
    }
  }

  /**
   * Fetch the current user's conversation history from conversationsPathValue.
   * Calls renderConversationHistory() on success or showConversationError() on failure.
   */
  async loadConversationHistory() {
    if (this.anonymousValue) return

    try {
      const response = await fetch(this.conversationsPathValue, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute('content'),
        },
      })

      if (!response.ok) {
        throw new Error('Failed to load conversations')
      }

      const conversations = await response.json()
      this.renderConversationHistory(conversations)
    } catch (error) {
      console.error('Error loading conversations:', error)
      this.showConversationError('Failed to load conversations')
    }
  }

  /**
   * Handle click on a conversation history item.
   * Reads data-conversation-id from the clicked element and loads that conversation.
   */
  loadConversationFromHistory(event) {
    const conversationId = event.currentTarget.dataset.conversationId
    if (conversationId) {
      this.loadConversation(conversationId)
    }
  }

  /**
   * Hook for child controllers to render conversation history list UI.
   */
  renderConversationHistory(_conversations) {
    // No-op by default; override in child controllers with UI-specific rendering
  }

  // ========================================
  // SESSION RESTORATION
  // ========================================

  /**
   * Restore a conversation from sessionStorage after page navigation.
   * Child controllers call this from afterConnect(). Override prepareRestore()
   * to open the UI (panel, modal, etc.) before the conversation loads.
   */
  async restoreFromSession() {
    if (this.anonymousValue) return

    const conversationId = sessionStorage.getItem(this._sessionStorageKey)
    if (!conversationId) return

    // Seed in-memory state so sendMessage targets the right conversation
    if (!this.currentConversation) {
      this.currentConversation = { conversation_token: conversationId }
    }

    this.prepareRestore()

    this.chatInputTarget.disabled = true
    try {
      await this.loadConversation(conversationId)
    } finally {
      this.chatInputTarget.disabled = false
    }
  }

  /**
   * Hook called before a restored conversation loads.
   * Override in child controllers to open the UI (panel, modal, etc.).
   */
  prepareRestore() {
    // No-op by default
  }

  /**
   * Sync sessionStorage with UI open/closed state.
   * Call from open/close methods in child controllers.
   * @param {boolean} isOpen - whether the UI (panel, modal) is now open
   */
  _syncConversationStorage(isOpen) {
    if (isOpen && this.currentConversation?.id) {
      sessionStorage.setItem(
        this._sessionStorageKey,
        String(this.currentConversation.id),
      )
    } else if (!isOpen) {
      sessionStorage.removeItem(this._sessionStorageKey)
    }
  }

  // ========================================
  // HELPER METHODS
  // ========================================

  /**
   * Check if the chat box is scrolled near the bottom
   * @param {number} threshold - Pixel threshold for "near bottom" detection
   */
  isNearBottom(threshold = 10) {
    const chatBox = this.chatBoxTarget
    return (
      chatBox.scrollTop + chatBox.clientHeight >=
      chatBox.scrollHeight - threshold
    )
  }

  /**
   * Helper method to escape HTML
   */
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  // ========================================
  // SUPPORT UI FLOW (shared between portal and admin)
  // ========================================

  /**
   * Start a new AI chat session. Clears any existing conversation
   * and transitions the UI from option buttons to chat view.
   * Override showChatView() in child controllers for UI-specific behavior.
   */
  startChat() {
    this.clearChat()
    this.showChatView()
  }

  /**
   * Close the support UI and open the Pendo Resource Center (Intercom).
   * Override closeSupportUI() in child controllers for UI-specific close behavior.
   */
  openPendoGuide() {
    this.closeSupportUI()
    this._clickPendoBadge()
  }

  /**
   * Hook: close the support UI (panel, modal, etc.) before opening Pendo.
   * Override in child controllers.
   */
  closeSupportUI() {
    // No-op by default
  }

  /**
   * Hook: transition from option buttons view to chat view.
   * Override in child controllers.
   */
  showChatView() {
    // No-op by default
  }

  /**
   * Programmatically click the hidden Pendo Resource Center badge
   * to open Intercom/support guide.
   */
  _clickPendoBadge() {
    const pendoBadge = document.querySelector(
      'button._pendo-resource-center-badge-container',
    )
    if (pendoBadge) {
      pendoBadge.click()
    }
  }

  /**
   * Toggle sources popup visibility when sources pill is clicked
   */
  toggleSourcesPopup(event) {
    const container = event.currentTarget.closest('.sources-pill-container')
    if (container) {
      container.classList.toggle('active-mobile')
    }
  }

  /**
   * Helper to scroll to the bottom of the modal
   */
  scrollToBottom() {
    this.chatBoxTarget.scrollTo({
      top: this.chatBoxTarget.scrollHeight,
      behavior: 'smooth',
    })
  }

  addTimestamp(messageBubble, sender) {
    const timestamp = document.createElement('div')
    timestamp.className = `ai-message-timestamp text-${sender === 'user' ? 'right' : 'left'} text-muted`
    timestamp.textContent = new Date().toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    })

    messageBubble.after(timestamp)
  }

  overridePersona() {
    return null
  }

  /**
   * Sets target/rel on all links within a container.
   * Delegates per-link decision to linkTargetForHref().
   */
  applyLinkTargets(container) {
    container.querySelectorAll('a').forEach((link) => {
      const target = this.linkTargetForHref(link.href)
      if (target) {
        link.setAttribute('target', target)
        link.setAttribute('rel', 'noopener noreferrer')
      }
    })
  }

  /**
   * Returns the link target for a given href.
   * Override in child controllers for context-specific behavior.
   */
  linkTargetForHref(_href) {
    return '_blank'
  }

  /**
   * Source identifier sent as a subscription param and validated server-side.
   * Override in child controllers (e.g. 'admin', 'sandbox').
   */
  subscriptionSource() {
    return 'portal'
  }

  extraPayloadData() {
    return {}
  }

  /**
   * Hook called when a new conversation is created and the backend returns its ID.
   * Override in child controllers to react (e.g. update the URL, set conversation state, etc.).
   */
  onConversationCreated(_conversationId) {
    // No-op by default
  }

  /**
   * Hook called after each assistant response with the message's database ID.
   * Override in child controllers to track the latest message for feedback, analytics, etc.
   */
  onAssistantResponse(_messageId) {
    // No-op by default
  }

  /**
   * Hook called when the server confirms a conversation (returns summary with token/ID).
   * Override in child controllers to track tokens for claiming, etc.
   */
  onConversationConfirmed(_summary) {
    // No-op by default
  }
}
